// Recursive loop expander that converts @for blocks into plain source text.
// Example: @for(i,[1,2]) { .m-${i}{...} } -> two generated selector blocks.
import type { ForLoopExpansionOptions, LoopScope } from './types'
import { matchForTokenAt, parseForLoop, resolveLoopValues } from './parser'
import { replaceLoopPlaceholders } from './placeholders'
import { createLoopIterationScope } from './scope'
import { transformPlainText } from './text-transform'
import { parseIfChain } from './if-parser'
import { evaluateIfCondition } from './if-condition-evaluate'
import type { IfBranch } from './types'
import { countLineAt, findNextControlFlowStart, handleLoopLimitViolation } from './expand-helpers'

// Build expanded output for all `@for(...) { ... }` / `for(...) { ... }` blocks recursively.
export function expandForLoops(source: string, options: ForLoopExpansionOptions): string {
  // Return recursively expanded source from empty root scope.
  return expandForLoopsInternal(source, options, {}, 1)
}

// Build expanded output for one source segment with inherited loop-scope values.
function expandForLoopsInternal(
  source: string,
  options: ForLoopExpansionOptions,
  scope: LoopScope,
  activeLoopProduct: number,
): string {
  let cursor = 0
  let output = ''

  // Scan source and expand each for-loop block in order.
  while (cursor < source.length) {
    const nextControlStart = findNextControlFlowStart(source, cursor)
    // Flush remaining plain text when no control token is found.
    if (nextControlStart === -1) {
      output += transformPlainText(source.slice(cursor), options.variables, options.arrays, options.objectVariables, options.warnings, scope)
      break
    } else {
      // Continue loop block parsing at detected token.
    }

    output += transformPlainText(source.slice(cursor, nextControlStart), options.variables, options.arrays, options.objectVariables, options.warnings, scope)
    const ifChain = parseIfChain(source, nextControlStart)
    // Evaluate and emit matching branch when scanner parsed an if-chain.
    if (ifChain) {
      output += expandIfChain(ifChain.branches, options, scope, activeLoopProduct)
      cursor = ifChain.endIndex
      continue
    } else {
      // Continue with for-loop parsing when token is not if-chain.
    }

    const loop = parseForLoop(source, nextControlStart)
    // Keep raw character when parser cannot build a valid loop block.
    if (!loop) {
      output += source[nextControlStart]
      cursor = nextControlStart + 1
      continue
    } else {
      // Continue resolving loop iterator values.
    }

    const resolvedSource = replaceLoopPlaceholders(loop.sourceExpression, scope, options.arrays, options.variables, options.objectVariables)
    const values = resolveLoopValues(resolvedSource, options.arrays, options.objectVariables)
    // Keep raw loop text when iterator source cannot be resolved.
    if (!values) {
      output += transformPlainText(source.slice(nextControlStart, loop.endIndex), options.variables, options.arrays, options.objectVariables, options.warnings, scope)
      cursor = loop.endIndex
      continue
    } else {
      // Continue emitting expanded loop body instances.
    }

    const loopLine = countLineAt(source, nextControlStart)
    // Enforce per-loop iteration budget before emitting loop body.
    if (values.length > options.resourceLimits.maxForIterationsPerLoop) {
      handleLoopLimitViolation(
        options,
        loopLine,
        `Loop iteration budget exceeded (${values.length}/${options.resourceLimits.maxForIterationsPerLoop}) in ${options.sourceId}.`,
      )
      cursor = loop.endIndex
      continue
    } else {
      // Continue nested-loop budget validation.
    }

    const nextLoopProduct = activeLoopProduct * values.length
    // Enforce nested loop expansion product budget.
    if (nextLoopProduct > options.resourceLimits.maxNestedLoopProduct) {
      handleLoopLimitViolation(
        options,
        loopLine,
        `Nested loop product exceeded (${nextLoopProduct}/${options.resourceLimits.maxNestedLoopProduct}) in ${options.sourceId}.`,
      )
      cursor = loop.endIndex
      continue
    } else {
      // Continue loop body emission when budgets are within limits.
    }

    // Emit one expanded body copy per resolved loop value.
    for (let valueIndex = 0; valueIndex < values.length; valueIndex += 1) {
      const value = values[valueIndex]
      const nestedScope = createLoopIterationScope(
        scope,
        loop.variableName,
        value,
        valueIndex,
        loop.indexVariableName,
      )
      output += expandForLoopsInternal(loop.body, options, nestedScope, nextLoopProduct)
    }

    cursor = loop.endIndex
  }

  // Return expanded output for current segment scope.
  return output
}

// Build output for one parsed if/elseif/else chain against the current loop scope.
function expandIfChain(
  branches: IfBranch[],
  options: ForLoopExpansionOptions,
  scope: LoopScope,
  activeLoopProduct: number,
): string {
  // Evaluate branches in order and return first matched branch output.
  for (const branch of branches) {
    if (branch.type === 'else') {
      // Return expanded else-body when no previous branch matched.
      return expandForLoopsInternal(branch.body, options, scope, activeLoopProduct)
    } else {
      // Continue condition evaluation for if/elseif branches.
    }

    const resolvedCondition = replaceLoopPlaceholders(branch.condition, scope, options.arrays, options.variables, options.objectVariables)
    const passed = evaluateIfCondition(resolvedCondition, scope, options.variables, options.objectVariables, options.arrays)
    if (passed) {
      // Return the first branch body that satisfies the evaluated condition.
      return expandForLoopsInternal(branch.body, options, scope, activeLoopProduct)
    } else {
      // Continue evaluation with next branch.
    }
  }

  // Return empty output when no branch condition matches.
  return ''
}
